export interface PriorityScoringConfig {
  marketPotential: number; // 0-25 points
  gtmReadyPercent: number; // 0-25 points
  status: number; // 0-20 points
  effortLevel: number; // 0-15 points
  marketCrowding: number; // 0-8 points
  marketType: number; // 0-7 points
  businessModel: number; // 0-5 points
  supportOverhead: number; // 0-5 points
  features: number; // 0-10 points
  competitors: number; // 0-3 points
  age: number; // 0-7 points
}

export interface Board {
  id: string;
  name: string;
  description?: string;
  boardType: 'product' | 'opportunity' | 'resource_manager';
  ownerId: string;
  createdAt: Date;
  updatedAt: Date;
  members: BoardMember[];
  priorityScoringConfig?: PriorityScoringConfig;
}

export interface BoardMember {
  userId: string;
  email: string;
  name?: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  joinedAt: Date;
}

export interface BoardInvitation {
  id: string;
  boardId: string;
  boardName?: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  invitedBy: string;
  invitedByEmail?: string;
  status: 'pending' | 'accepted' | 'declined' | 'cancelled';
  createdAt: Date;
  expiresAt: Date;
}

export interface BoardShareLink {
  id: string;
  boardId: string;
  boardName?: string;
  createdBy: string;
  createdByEmail?: string;
  role: 'admin' | 'editor' | 'viewer';
  isActive: boolean;
  expiresAt?: Date;
  createdAt: Date;
  accessCount: number;
}

export interface Feature {
  id: string;
  name: string;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Blocked' | 'On Hold';
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

/** Discovery / PMF notes from collaborative sessions (stored on the product). */
export interface ProductMarketFitQA {
  id: string;
  question: string;
  answer?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByEmail?: string;
}

/** How a project / product is billed (e.g. for budgets vs allocations). */
export type ProductPricingModel =
  | 'time_and_materials'
  | 'fixed_price'
  | 'internal'
  | 'product';

export const PRODUCT_PRICING_MODEL_LABELS: Record<ProductPricingModel, string> = {
  time_and_materials: 'Time & materials',
  fixed_price: 'Fixed price',
  internal: 'Internal',
  product: 'Product',
};

/** Stable order for every pricing dropdown (includes all models exactly once). */
export const PRODUCT_PRICING_MODEL_ORDER: readonly ProductPricingModel[] = [
  'time_and_materials',
  'fixed_price',
  'internal',
  'product',
];

export const PRODUCT_PRICING_MODEL_OPTIONS: ReadonlyArray<{
  value: ProductPricingModel;
  label: string;
}> = PRODUCT_PRICING_MODEL_ORDER.map((value) => ({
  value,
  label: PRODUCT_PRICING_MODEL_LABELS[value],
}));

export function getProductPricingModelLabel(model: string | null | undefined): string {
  if (model != null && model in PRODUCT_PRICING_MODEL_LABELS) {
    return PRODUCT_PRICING_MODEL_LABELS[model as ProductPricingModel];
  }
  if (model == null || model === '') {
    return PRODUCT_PRICING_MODEL_LABELS.time_and_materials;
  }
  return model;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  aiSummary?: string;
  effortLevel: 'Low' | 'Medium' | 'High';
  marketPotential: 'Low' | 'Medium' | 'High';
  gtmReadyPercent: number;
  priorityScore?: number;
  status: 'Ideas' | 'Research' | 'Development' | 'Testing' | 'Launch';
  features: Feature[];
  competitors?: string[];
  marketCrowding?: 'No Competition' | 'Low Competition' | 'Medium Competition' | 'High Competition';
  marketType?: 'Blue Ocean' | 'Red Ocean';
  businessModel?: 'SaaS' | 'Marketplace' | 'E-commerce' | 'Mobile App' | 'API/Platform' | 'Hardware' | 'Consulting' | 'Other';
  supportOverhead?: 'Low' | 'Medium' | 'High';
  industryAreas?: string[];
  links?: Array<{
    id: string;
    title: string;
    url: string;
    type: 'design' | 'documentation' | 'research' | 'competitor' | 'other';
  }>;
  owner?: string;
  createdAt: Date;
  updatedAt: Date;
  columnId: string;
  boardId: string;
  createdBy: string;
  commentCount?: number;
  taskCount?: number;
  /** Project start date (e.g. for Resource Manager projects). */
  startDate?: Date;
  /** Project end date (e.g. for Resource Manager projects). */
  endDate?: Date;
  /** Billing model for the project (Resource Manager and product boards). */
  pricingModel?: ProductPricingModel;
  /** Questions and answers logged while refining product–market fit. */
  marketFitQa?: ProductMarketFitQA[];
}

export interface FileAttachment {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  uploadedAt: Date;
  uploadedBy: string;
  storagePath?: string; // Optional for backward compatibility
}

export interface Opportunity {
  id: string;
  name: string;
  description?: string;
  aiSummary?: string;
  effortLevel: 'Low' | 'Medium' | 'High';
  marketPotential: 'Low' | 'Medium' | 'High';
  gtmReadyPercent: number;
  priorityScore?: number;
  status: 'Prospecting' | 'Qualified' | 'Proposal' | 'Negotiation' | 'Closed Won' | 'Closed Lost';
  opportunityType: 'RFP' | 'Service Contract' | 'Partnership' | 'Consulting' | 'Training' | 'Support' | 'Other';
  clientSize: 'Startup' | 'SMB' | 'Enterprise' | 'Government' | 'Non-Profit';
  estimatedValue: number;
  winProbability: number;
  competitors?: string[];
  marketCrowding?: 'No Competition' | 'Low Competition' | 'Medium Competition' | 'High Competition';
  marketType?: 'Blue Ocean' | 'Red Ocean';
  businessModel?: 'SaaS' | 'Marketplace' | 'E-commerce' | 'Mobile App' | 'API/Platform' | 'Hardware' | 'Consulting' | 'Other';
  supportOverhead?: 'Low' | 'Medium' | 'High';
  industryAreas?: string[];
  attachments?: FileAttachment[];
  createdAt: Date;
  updatedAt: Date;
  columnId: string;
  boardId: string;
  createdBy: string;
  commentCount?: number;
  taskCount?: number;
}

export interface Column {
  id: string;
  title: string;
  color: string;
  order: number;
  boardId: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  photoURL?: string;
}

export interface Comment {
  id: string;
  productId: string;
  boardId: string;
  content: string;
  createdBy: string;
  createdByEmail: string;
  createdAt: Date;
  updatedAt: Date;
  mentions?: string[]; // Array of user IDs that are mentioned in the comment
}

export interface Activity {
  id: string;
  boardId: string;
  productId?: string;
  type: 'product_created' | 'product_updated' | 'product_moved' | 'comment_added' | 'priority_changed' | 'status_changed' | 'member_joined' | 'member_left';
  description: string;
  createdBy: string;
  createdByEmail: string;
  createdAt: Date;
  metadata?: {
    oldValue?: string;
    newValue?: string;
    columnId?: string;
    priorityScore?: number;
  };
}

export interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

// Default industry areas for product categorization
export const DEFAULT_INDUSTRY_AREAS = [
  'Technology',
  'Healthcare',
  'Finance',
  'Education',
  'Retail',
  'Manufacturing',
  'Transportation',
  'Energy',
  'Real Estate',
  'Entertainment',
  'Media',
  'Food & Beverage',
  'Travel & Tourism',
  'Automotive',
  'Aerospace',
  'Defense',
  'Agriculture',
  'Construction',
  'Telecommunications',
  'Legal',
  'Marketing & Advertising',
  'Human Resources',
  'Supply Chain',
  'Logistics',
  'Environmental',
  'Sports & Fitness',
  'Gaming',
  'Social Media',
  'E-commerce',
  'SaaS',
  'Mobile Apps',
  'IoT',
  'AI & Machine Learning',
  'Blockchain',
  'Cybersecurity',
  'Cloud Computing',
  'Data Analytics',
  'Biotechnology',
  'Pharmaceuticals',
  'Non-profit',
  'Government',
  'Consulting',
  'Professional Services',
  'Other'
];

export interface Notification {
  id: string;
  userId: string;
  type: 'mention' | 'comment' | 'invitation' | 'board_update';
  title: string;
  message: string;
  boardId?: string;
  productId?: string;
  commentId?: string;
  createdBy?: string;
  createdByEmail?: string;
  isRead: boolean;
  createdAt: Date;
}

export interface Task {
  id: string;
  productId: string; // Tasks are only associated with products
  boardId: string;
  title: string;
  description?: string;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Blocked' | 'On Hold';
  priority: 'Low' | 'Medium' | 'High';
  owner?: string; // User ID of the task owner
  ownerEmail?: string; // Email of the task owner
  link?: string; // Optional link related to the task
  attachment?: {
    id: string;
    name: string;
    url: string;
    size: number;
    type: string;
  };
  createdBy: string;
  createdByEmail: string;
  createdAt: Date;
  updatedAt: Date;
  dueDate?: Date;
}

/** Configurable job role for resources; bill/cost rates support project budget estimates. */
export interface ResourceRole {
  id: string;
  boardId: string;
  name: string;
  /** Client bill rate in USD per hour. */
  billRatePerHour: number;
  /** Internal cost rate in USD per hour. */
  costRatePerHour: number;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export interface Resource {
  id: string;
  name: string;
  /** Role label; should match a `ResourceRole.name` on this board when roles are configured. */
  role: string;
  boardId: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  /** Unfilled staffing slot; replace with a real person in Assignments (by project). */
  isPlaceholder?: boolean;
}

/** Default role names used when seeding a board or when no role catalog exists yet. */
export const DEFAULT_RESOURCE_ROLE_NAMES = [
  'Developer',
  'Project Manager',
  'Product Manager',
  'Designer',
  'Architect',
  'Integration',
] as const;

/** Legacy flat full-time hours per month (hours). Used where no calendar month context exists (e.g. simple % FTE). */
export const FULL_TIME_HOURS_PER_MONTH = 160;

export interface ResourceAllocation {
  id: string;
  resourceId: string;
  productId: string;
  boardId: string;
  /** Allocation as percentage (0-100). Derived from hoursPerMonth when using monthly hours. */
  percent: number;
  /** Monthly hours committed to this project. 160 = full time. Primary for resource manager. */
  hoursPerMonth?: number;
  /** LOE as hours per week (legacy). Prefer hoursPerMonth. */
  hoursPerWeek?: number;
  /** 'percent' | 'hours' (legacy). Use hoursPerMonth for new data. */
  loeType?: 'percent' | 'hours';
  /** Start date of this assignment. */
  startDate?: Date;
  /** End date of this assignment. */
  endDate?: Date;
  /** Hours per month by "YYYY-MM". Used for 12-month allocation planning. */
  monthlyHours?: Record<string, number>;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}
